import React, { useState, forwardRef, useImperativeHandle } from 'react';
import { useTranslation } from 'react-i18next';
import { useConsent } from '../../context/ConsentContext';
import { ConsentState } from '../../utils/gtag';
import '../../styles/consent.css';

export interface ConsentManagerHandle {
  save: () => void;
}

interface Props {
  hideSaveButton?: boolean;
  onDirty?: () => void;
}

const ConsentManager = forwardRef<ConsentManagerHandle, Props>(({ hideSaveButton = false, onDirty }, ref) => {
  const { t } = useTranslation('consent');
  const { consent, updateConsent } = useConsent();
  const [local, setLocal] = useState<ConsentState>({ ...consent });
  const [saved, setSaved] = useState(false);

  const toggle = (key: keyof ConsentState) => {
    setSaved(false);
    setLocal(prev => ({ ...prev, [key]: !prev[key] }));
    onDirty?.();
  };

  const save = () => {
    updateConsent(local);
    setSaved(true);
  };

  useImperativeHandle(ref, () => ({ save }));

  return (
    <div className="consent-manager">
      <h3 className="consent-manager__title">{t('manager.title')}</h3>
      <p className="consent-manager__desc">{t('manager.desc')}</p>

      <div className="consent-modal__categories">
        <div className="consent-category">
          <div className="consent-category__info">
            <p className="consent-category__name">{t('categories.essential.name')}</p>
            <p className="consent-category__desc">{t('categories.essential.desc')}</p>
          </div>
          <label className="consent-toggle">
            <input type="checkbox" checked disabled />
            <span className="consent-toggle__track" />
          </label>
        </div>

        <div className="consent-category">
          <div className="consent-category__info">
            <p className="consent-category__name">{t('categories.analytics.name')}</p>
            <p className="consent-category__desc">{t('categories.analytics.desc')}</p>
          </div>
          <label className="consent-toggle">
            <input type="checkbox" checked={local.analytics} onChange={() => toggle('analytics')} />
            <span className="consent-toggle__track" />
          </label>
        </div>

        <div className="consent-category">
          <div className="consent-category__info">
            <p className="consent-category__name">{t('categories.marketing.name')}</p>
            <p className="consent-category__desc">{t('categories.marketing.desc')}</p>
          </div>
          <label className="consent-toggle">
            <input type="checkbox" checked={local.marketing} onChange={() => toggle('marketing')} />
            <span className="consent-toggle__track" />
          </label>
        </div>

        <div className="consent-category">
          <div className="consent-category__info">
            <p className="consent-category__name">{t('categories.preferences.name')}</p>
            <p className="consent-category__desc">{t('categories.preferences.desc')}</p>
          </div>
          <label className="consent-toggle">
            <input type="checkbox" checked={local.preferences} onChange={() => toggle('preferences')} />
            <span className="consent-toggle__track" />
          </label>
        </div>
      </div>

      {!hideSaveButton && (
        <>
          <button className="consent-manager__save" onClick={save}>{t('manager.save')}</button>
          {saved && <span className="consent-manager__saved">{t('manager.saved')}</span>}
        </>
      )}
    </div>
  );
});

export default ConsentManager;
